import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, HostListener, ViewChild, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LanguageService } from 'src/app/services/account/language.service';
import { StackedModalsService } from 'src/app/services/modal/stacked-modals.service';
import { TutorialPlacement, TutorialService, TutorialStep, TutorialStepDefinition } from 'src/app/services/core/tutorial.service';

interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** Gap between the target and the card, and the smallest distance the card keeps from the viewport edge. */
const CARD_GAP = 14;
const VIEWPORT_EDGE = 10;
/** The arrow must stay clear of the card's rounded corners. */
const ARROW_INSET = 22;

/**
 * Guided tutorial overlay: a coach mark pointing at the element the user has
 * to press next, with a pulsing spotlight ring around it. The overlay never
 * blocks the app — only the card itself takes pointer events — and it sits
 * below the modal layer, so the navigation disclaimer and the stop
 * confirmation still open on top of it. Rendered once by MainComponent;
 * TutorialService decides what (if anything) is shown.
 */
@Component({
  standalone: true,
  selector: 'app-tutorial',
  templateUrl: './tutorial.component.html',
  styleUrls: ['./tutorial.component.scss'],
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TutorialComponent {
  public readonly tutorialService = inject(TutorialService);
  public readonly languageService = inject(LanguageService);
  private readonly stackedModalsService = inject(StackedModalsService);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('card') private readonly cardRef?: ElementRef<HTMLElement>;

  public readonly TutorialStep = TutorialStep;
  /**
   * Wrapped in a one-element list so the template's @for re-creates the card
   * on every step change, which replays the enter animation.
   */
  public readonly steps = computed<TutorialStepDefinition[]>(() => {
    const def = this.tutorialService.definition();
    return def ? [def] : [];
  });
  public readonly stepDots = computed(() => Array.from({ length: this.tutorialService.stepCount }, (_, i) => i + 1));

  public readonly targetRect = signal<TargetRect | null>(null);
  /** True while the card points at the step's fallback target (or floats because neither target is on screen). */
  public readonly usingFallback = signal<boolean>(false);
  public readonly targetRadius = signal<string>('');
  public readonly placement = signal<TutorialPlacement>('center');
  public readonly cardPosition = signal<{ top: number, left: number } | null>(null);
  /** Arrow offset along the card edge facing the target (px from the card's top-left). */
  public readonly arrowOffset = signal<number>(0);
  /**
   * True while the card would cover an element marked `data-tutorial-avoid`
   * (the search results list): the card fades out and comes back when the
   * element goes away, rather than sitting on top of what the user is using.
   */
  public readonly yielding = signal<boolean>(false);

  private targetElement: HTMLElement | null = null;
  private frame: number | null = null;
  private lastLayoutKey = '';
  private readonly onDocumentClick = (event: Event) => {
    if (this.targetElement && !this.usingFallback() && event.target instanceof Node && this.targetElement.contains(event.target)) {
      this.tutorialService.completeTargetClick();
    }
  };

  constructor() {
    effect(() => {
      const def = this.tutorialService.definition();
      this.resetLayout(def?.placement ?? 'center');
      if (def) {
        this.startTracking();
      } else {
        this.stopTracking();
      }
    });

    this.destroyRef.onDestroy(() => this.stopTracking());
  }

  @HostListener('document:keydown.escape')
  public onEscape(): void {
    // A modal on top owns Escape; the tutorial waits underneath it.
    if (this.tutorialService.active() && !this.stackedModalsService.isModalOpen()) {
      this.tutorialService.stop();
    }
  }

  public skip(): void {
    this.tutorialService.stop();
  }

  public finish(): void {
    this.tutorialService.stop();
  }

  private resetLayout(placement: TutorialPlacement): void {
    this.targetElement = null;
    this.lastLayoutKey = '';
    this.targetRect.set(null);
    this.targetRadius.set('');
    this.usingFallback.set(false);
    this.yielding.set(false);
    this.placement.set(placement);
    this.cardPosition.set(null);
    this.arrowOffset.set(0);
  }

  // The target appears asynchronously (the GO button renders with the route,
  // the stop button with the navigation card) and moves around (panel drag on
  // mobile, plan card height changes), so its rect is re-read every frame
  // while a step is showing. It is a single getBoundingClientRect per frame
  // and signals only change when the layout actually did.
  private startTracking(): void {
    document.addEventListener('click', this.onDocumentClick, true);
    if (this.frame === null) {
      const tick = () => {
        this.updateLayout();
        this.frame = requestAnimationFrame(tick);
      };
      this.frame = requestAnimationFrame(tick);
    }
  }

  private stopTracking(): void {
    document.removeEventListener('click', this.onDocumentClick, true);
    if (this.frame !== null) {
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  }

  private updateLayout(): void {
    const def = this.tutorialService.definition();
    const card = this.cardRef?.nativeElement;
    if (!def || !card) return;

    this.yielding.set(this.overlapsAvoidedElement(card));

    if (!def.target) {
      // Floating card, positioned by CSS.
      if (this.cardPosition() === null) {
        this.cardPosition.set({ top: 0, left: 0 });
      }
      return;
    }

    const fallback = this.resolveTargetElement(def.target, def.fallback?.target);
    const rect = this.targetElement?.getBoundingClientRect() ?? null;
    const visible = !!rect && rect.width > 0 && rect.height > 0;

    const key = visible ? this.layoutKey(rect, card, fallback) : 'none';
    if (key === this.lastLayoutKey) return;
    this.lastLayoutKey = key;

    if (!visible) {
      // Nothing to point at (yet): show the card mid-screen without a spotlight.
      this.targetRect.set(null);
      this.usingFallback.set(true);
      this.placement.set('center');
      this.cardPosition.set({ top: 0, left: 0 });
      return;
    }

    this.spotlightTarget(def, card, rect, fallback);
  }

  // Placement follows the targets' presence in the DOM, so the card also
  // falls back (POI card dismissed → search box again) without any event.
  // Sets targetElement and returns whether it is the fallback target.
  private resolveTargetElement(target: string, fallbackTarget: string | undefined): boolean {
    this.targetElement = this.findTarget(target);
    if (!this.targetElement && fallbackTarget) {
      this.targetElement = this.findTarget(fallbackTarget);
      return true;
    }
    return false;
  }

  private layoutKey(rect: DOMRect, card: HTMLElement, fallback: boolean): string {
    return [rect.top, rect.left, rect.width, rect.height, card.offsetWidth, card.offsetHeight, window.innerWidth, window.innerHeight].map(Math.round).join(',') + (fallback ? ',f' : '');
  }

  private spotlightTarget(def: TutorialStepDefinition, card: HTMLElement, rect: DOMRect, fallback: boolean): void {
    const target: TargetRect = { top: rect.top, left: rect.left, width: rect.width, height: rect.height };
    const radius = getComputedStyle(this.targetElement!).borderRadius;
    const placement = fallback ? def.fallback!.placement : def.placement;
    const container = fallback ? def.fallback!.container : def.container;
    this.targetRect.set(target);
    this.usingFallback.set(fallback);
    this.targetRadius.set(radius && radius !== '0px' ? radius : '10px');
    this.positionCard(placement, target, this.findContainerRect(container, this.targetElement!), card.offsetWidth, card.offsetHeight);
  }

  private overlapsAvoidedElement(card: HTMLElement): boolean {
    const rect = card.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return false;
    return Array.from(document.querySelectorAll<HTMLElement>('[data-tutorial-avoid]')).some(el => {
      const r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0 &&
        r.left < rect.right && r.right > rect.left && r.top < rect.bottom && r.bottom > rect.top;
    });
  }

  private findTarget(name: string): HTMLElement | null {
    const candidates = document.querySelectorAll<HTMLElement>(`[data-tutorial-target="${name}"]`);
    for (const el of Array.from(candidates)) {
      if (el.offsetParent !== null || getComputedStyle(el).position === 'fixed') {
        return el;
      }
    }
    return null;
  }

  private findContainerRect(name: string | undefined, target: HTMLElement): TargetRect | null {
    const el = name ? target.closest<HTMLElement>(`[data-tutorial-container="${name}"]`) : null;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return { top: rect.top, left: rect.left, width: rect.width, height: rect.height };
  }

  private positionCard(preferred: TutorialPlacement, target: TargetRect, container: TargetRect | null, cardWidth: number, cardHeight: number): void {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const cx = target.left + target.width / 2;
    const cy = target.top + target.height / 2;
    // The card clears the whole container on the side it is placed at; the
    // arrow still points at the target itself.
    const clear = container ?? target;
    const top0 = clear.top;
    const left0 = clear.left;
    const bottom = clear.top + clear.height;
    const right = clear.left + clear.width;

    const fits: Record<Exclude<TutorialPlacement, 'center'>, boolean> = {
      top: top0 - CARD_GAP - cardHeight >= VIEWPORT_EDGE,
      bottom: bottom + CARD_GAP + cardHeight <= vh - VIEWPORT_EDGE,
      left: left0 - CARD_GAP - cardWidth >= VIEWPORT_EDGE,
      right: right + CARD_GAP + cardWidth <= vw - VIEWPORT_EDGE,
    };
    const opposite: Record<string, Exclude<TutorialPlacement, 'center'>> = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };
    const first = preferred === 'center' ? 'top' : preferred;
    // Below before above: the panels the targets live in (planning panel on
    // mobile, POI card) extend upwards from the button, so "bottom" is the
    // side that is usually free.
    const order: Exclude<TutorialPlacement, 'center'>[] = [first, opposite[first], 'bottom', 'top', 'right', 'left'];
    const placement = order.find(p => fits[p]) ?? first;

    let top: number;
    let left: number;
    switch (placement) {
      case 'top':
        top = top0 - CARD_GAP - cardHeight;
        left = cx - cardWidth / 2;
        break;
      case 'bottom':
        top = bottom + CARD_GAP;
        left = cx - cardWidth / 2;
        break;
      case 'left':
        top = cy - cardHeight / 2;
        left = left0 - CARD_GAP - cardWidth;
        break;
      default:
        top = cy - cardHeight / 2;
        left = right + CARD_GAP;
    }

    left = Math.min(Math.max(left, VIEWPORT_EDGE), Math.max(VIEWPORT_EDGE, vw - VIEWPORT_EDGE - cardWidth));
    top = Math.min(Math.max(top, VIEWPORT_EDGE), Math.max(VIEWPORT_EDGE, vh - VIEWPORT_EDGE - cardHeight));

    // The arrow keeps pointing at the target's centre even when the card was
    // pushed sideways to stay on screen.
    const arrow = placement === 'top' || placement === 'bottom'
      ? Math.min(Math.max(cx - left, ARROW_INSET), cardWidth - ARROW_INSET)
      : Math.min(Math.max(cy - top, ARROW_INSET), cardHeight - ARROW_INSET);

    this.placement.set(placement);
    this.arrowOffset.set(arrow);
    this.cardPosition.set({ top, left });
  }
}
